import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import 'react-calendar/dist/Calendar.css';
import CalendarTodayRoundedIcon from '@material-ui/icons/CalendarTodayOutlined';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import RadioGroup from '@material-ui/core/RadioGroup';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Popper from '@material-ui/core/Popper';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Button from '@material-ui/core/Button';
import { RadioButton } from 'components/buttons/RadioButtons';
import WidgetButton from 'components/buttons/WidgetButton';
import ExpansionPanel from 'components/ExpansionPanel';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import ConditionalCustomDate from 'components/ConditionalCustomDate/ConditionalCustomDate';
import { dynamicDateOptions, dateMatchTypeOptions, customDateMatchTypeOptions } from './content';
import { calculateDynamicTimeIntervals } from 'utils/widget-helpers';

const useStyles = makeStyles(() => ({
  button: {
    marginRight: '20px'
  },
  popper: {
    zIndex: '1000'
  },
  icon: {
    fontSize: 'medium'
  }
}));

const DateButton = ({
  startDate,
  endDate,
  onSubmit,
  dateRangeTypeId,
  dateMatchType,
  isDynamicDate,
  startPeriod,
  endPeriod,
  apiState,
  globalPickerDateState,
  setBaseWidgetDates
}) => {
  const classes = useStyles();
  const isInvalidDateFormat = dateRangeTypeId === 1 || 4;
  const patchStartDateVar = isInvalidDateFormat ? '2019-01-01' : startPeriod;
  const patchEndDateVar = isInvalidDateFormat ? moment().format('YYYY-MM-DD') : endPeriod;
  const initialExpansion = 'customDate';
  const [startDateState, setStartDateStateDate] = useState(
    new Date(startDate || patchStartDateVar)
  );
  const [endDateState, setEndDateStateDate] = useState(new Date(endDate || patchEndDateVar));
  const [anchorEl, setAnchorEl] = useState(null);
  const [expanded, setExpanded] = useState(initialExpansion);
  const [dateRangeIdValue, setDateRangeIdValue] = useState(`${dateRangeTypeId}`);
  const [dateMatchTypeValue, setDateMatchTypeValue] = useState(`${dateMatchType}`);
  const [customDateMatchTypeValue, setCustomDateMatchTypeValue] = useState(
    `${2}`
  );

  const { isLoading } = apiState;
  const [showDashboardDate, setShowDashboardDate] = useState(!!dateRangeTypeId);
  const handleExpansionClick = event => {
    setAnchorEl(event.currentTarget);
  };

  useEffect(() => {
    if (startDate) {
      setStartDateStateDate(new Date(startDate));
    }
  }, [startDate]);

  useEffect(() => {
    if (endDate) {
      setEndDateStateDate(new Date(endDate));
    }
  }, [endDate]);

  useEffect(() => {
    setExpanded(initialExpansion);
  }, [isDynamicDate, initialExpansion]);

  useEffect(() => {
    if (globalPickerDateState && globalPickerDateState.startDate) {
      setStartDateStateDate(new Date(globalPickerDateState.startDate));
    }
    if (globalPickerDateState && globalPickerDateState.endDate) {
      setEndDateStateDate(new Date(globalPickerDateState.endDate));
    }
  }, [globalPickerDateState])

  const handleExpandedChange = event => {
    if (event.currentTarget.id === expanded) {
      setExpanded('');
      return;
    }
    setExpanded(event.currentTarget.id);
  };

  const handleClickAway = () => {
    setAnchorEl(null);
  };

  const onCustomDateSubmit = () => {
    const newData = {
      startDate: moment(startDateState).format('YYYY-MM-DD'),
      endDate: moment(endDateState).format('YYYY-MM-DD'),
      dateRangeTypeId: 0,
      customDateRangeMatchType: Number(customDateMatchTypeValue)
    };
    onSubmit(newData);
    setBaseWidgetDates(startDateState, endDateState);
    setDateRangeIdValue('');
    handleClickAway();
  };

  const onChosenDateSubmit = () => {
    const nulledData = {
      startDate: null,
      endDate: null,
      dateRangeTypeId: null
    };
    const newData = {
      startDate: moment(startDateState).format('YYYY-MM-DD'),
      endDate: moment(endDateState).format('YYYY-MM-DD'),
      dateRangeTypeId: 0
    };

    onSubmit(showDashboardDate ? nulledData : newData);
    setBaseWidgetDates(startDateState, endDateState);
    setDateRangeIdValue('');
    handleClickAway();
  };

  const onDateRangeIdSubmit = () => {
    const newData = { dateRangeTypeId: Number(dateRangeIdValue) };
    onSubmit(newData);

    const calculatedDates = calculateDynamicTimeIntervals(newData.dateRangeTypeId);
    setBaseWidgetDates(new Date(calculatedDates.startPeriod), new Date(calculatedDates.endPeriod));
    handleClickAway();
  };

  const onDateMatchTypeSubmit = () => {
    if (!dateMatchTypeValue) return;

    const newData = { dateMatchType: Number(dateMatchTypeValue) };
    onSubmit(newData);
    handleClickAway();
  };

  const handleWidgetDateSource = () => {
    setShowDashboardDate(!showDashboardDate);
  };

  const handleSetStartDate = newDate => {
    setStartDateStateDate(newDate);
  };

  const handleSetEndDate = newDate => {
    setEndDateStateDate(newDate);
  };

  const handleDateRangeIdChange = event => {
    setDateRangeIdValue(event.target.value);
  };

  const handleDateMatchTypeChange = event => {
    setDateMatchTypeValue(event.target.value);
  };

  const handleCustomDateMatchTypeChange = event => {
    setCustomDateMatchTypeValue(event.currentTarget.value);
  };

  const handleClick = event => {
    if (event.target.value === dateRangeIdValue && event.target.value !== undefined) {
      setDateRangeIdValue(0);
    } else {
      setDateRangeIdValue(event.target.value);
    }
  };

  return (
    <>
      <WidgetButton onClick={handleExpansionClick}>
        <CalendarTodayRoundedIcon className={classes.icon} />
      </WidgetButton>
      <Popper
        open={!!anchorEl}
        placement="bottom-end"
        anchorEl={anchorEl}
        transition
        disablePortal
        className={classes.popper}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper>
            <ExpansionPanel
              title="Custom Date"
              id="customDate"
              expanded={expanded}
              handleChange={handleExpandedChange}
            >
              <Box p={2} maxWidth="360px">
                <Grid
                  container
                  direction="row"
                  justify="flex-start"
                  alignItems="flex-start"
                  spacing={1}
                >
                  {customDateMatchTypeOptions &&
                    customDateMatchTypeOptions.map(option => (
                      <Grid item xs={4} key={option.title}>
                        <Button
                          variant={
                            customDateMatchTypeValue === option.value ? 'contained' : 'outlined'
                          }
                          color="primary"
                          value={option.value}
                          onClick={handleCustomDateMatchTypeChange}
                        >
                          {option.title}
                        </Button>
                      </Grid>
                    ))}
                </Grid>
                <Box>
                  <ConditionalCustomDate
                    handleSetStartDate={handleSetStartDate}
                    startDateState={startDateState}
                    endDateState={endDateState}
                    handleSetEndDate={handleSetEndDate}
                    component={customDateMatchTypeValue}
                  />
                </Box>
              </Box>

              <Box px={1} pb={1}>
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.button}
                  onClick={onCustomDateSubmit}
                  disabled={isLoading}
                >
                  Save
                </Button>
                <Button color="primary" className={classes.button} onClick={handleClickAway}>
                  Cancel
                </Button>
              </Box>
            </ExpansionPanel>
            <ExpansionPanel
              title="Dynamic Date"
              id="dynamicDate"
              expanded={expanded}
              handleChange={handleExpandedChange}
            >
              <Box p={2} maxWidth="360px">
                <h3>Show me data for:</h3>
                <RadioGroup
                  aria-label="quiz"
                  name="quiz"
                  value={dateRangeIdValue}
                  onChange={handleDateRangeIdChange}
                >
                  <Grid
                    container
                    direction="row"
                    justify="flex-start"
                    alignItems="flex-start"
                    spacing={1}
                  >
                    {dynamicDateOptions &&
                      dynamicDateOptions.map(option => (
                        <Grid item xs={6} key={option.title}>
                          <FormControlLabel
                            value={option.value}
                            control={<Radio color="primary" onClick={handleClick} />}
                            label={option.title}
                          />
                        </Grid>
                      ))}
                  </Grid>
                </RadioGroup>
              </Box>
              <Box px={1} pb={1}>
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.button}
                  onClick={onDateRangeIdSubmit}
                  disabled={isLoading}
                >
                  Save
                </Button>
                <Button color="primary" className={classes.button} onClick={handleClickAway}>
                  Cancel
                </Button>
              </Box>
            </ExpansionPanel>
            <ExpansionPanel
              title="Date Match Type"
              id="dateMatchType"
              expanded={expanded}
              handleChange={handleExpandedChange}
            >
              <Box p={2} maxWidth="360px">
                <h3>Choose:</h3>
                <RadioGroup
                  aria-label="quiz"
                  name="quiz"
                  value={dateMatchTypeValue}
                  onChange={handleDateMatchTypeChange}
                >
                  <Grid
                    container
                    direction="row"
                    justify="flex-start"
                    alignItems="flex-start"
                    spacing={1}
                  >
                    {dateMatchTypeOptions &&
                      dateMatchTypeOptions.map(option => (
                        <Grid item xs={6} key={option.title}>
                          <RadioButton title={option.title} value={option.value} />
                        </Grid>
                      ))}
                  </Grid>
                </RadioGroup>
              </Box>
              <Box px={1} pb={1}>
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.button}
                  onClick={onDateMatchTypeSubmit}
                >
                  Save
                </Button>
                <Button color="primary" className={classes.button} onClick={handleClickAway}>
                  Cancel
                </Button>
              </Box>
            </ExpansionPanel>
            <ExpansionPanel
              title="Use Dashboard Dates"
              id="dashboardDate"
              expanded={expanded}
              handleChange={handleExpandedChange}
            >
              <Box p={2} maxWidth="360px">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showDashboardDate}
                      onChange={handleWidgetDateSource}
                      inputProps={{ 'aria-label': 'primary checkbox' }}
                    />
                  }
                  label="Use dashboard date set"
                  labelPlacement="end"
                />
              </Box>
              <Box px={1} pb={1}>
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.button}
                  onClick={onChosenDateSubmit}
                >
                  Save
                </Button>
                <Button color="primary" className={classes.button} onClick={handleClickAway}>
                  Cancel
                </Button>
              </Box>
            </ExpansionPanel>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

DateButton.propTypes = {
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  apiState: PropTypes.shape({ isLoading: PropTypes.bool }),
  dateRangeTypeId: PropTypes.number.isRequired,
  dateMatchType: PropTypes.number.isRequired,
  customDateMatchType: PropTypes.number.isRequired,
  isDynamicDate: PropTypes.bool.isRequired,
  startPeriod: PropTypes.string,
  endPeriod: PropTypes.string
};

DateButton.defaultProps = {
  startDate: null,
  endDate: null,
  startPeriod: null,
  endPeriod: null,
  apiState: {
    isLoading: false
  }
};

export default DateButton;
